/**
 * Checks an uploaded file's leading bytes ("magic number") against its extension, so a file
 * renamed to .pdf/.doc/... is caught before upload. The backend repeats this check (and more):
 * this is only for a quicker, clearer error. Keep it in step with
 * JobApplicationGuard::contentMatchesExtension in the Onboarding backend.
 */

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0): boolean =>
  signature.every((byte, i) => bytes[offset + i] === byte);

const containsAscii = (bytes: Uint8Array, text: string): boolean => {
  const codes = Array.from(text).map((c) => c.charCodeAt(0));
  for (let i = 0; i <= bytes.length - codes.length; i++) {
    if (startsWith(bytes, codes, i)) {
      return true;
    }
  }
  return false;
};

const readHead = (file: File, length: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, length));
  });

export const fileExtension = (file: File): string => {
  const parts = file.name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

export const fileSignatureMatches = async (file: File): Promise<boolean> => {
  let head: Uint8Array;
  try {
    head = await readHead(file, 1024);
  } catch (_error) {
    return false;
  }

  switch (fileExtension(file)) {
    case "pdf":
      return containsAscii(head, "%PDF-");
    case "doc":
      return startsWith(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
    case "docx":
      return startsWith(head, [0x50, 0x4b, 0x03, 0x04]);
    case "rtf": {
      let start = startsWith(head, [0xef, 0xbb, 0xbf]) ? 3 : 0;
      while ([0x20, 0x09, 0x0d, 0x0a].includes(head[start])) {
        start++;
      }
      return startsWith(head, [0x7b, 0x5c, 0x72, 0x74, 0x66], start); // "{\rtf"
    }
    case "mdi":
      return startsWith(head, [0x45, 0x50, 0x2a, 0x00])
        || startsWith(head, [0x49, 0x49, 0x2a, 0x00])
        || startsWith(head, [0x4d, 0x4d, 0x00, 0x2a]);
    case "txt":
      if (startsWith(head, [0xff, 0xfe]) || startsWith(head, [0xfe, 0xff])) {
        return true;
      }
      return !head.includes(0);
    default:
      return false;
  }
};
